import { useState, useEffect } from "react";
import { Upload } from "lucide-react";
import { apiClient } from "../../apiclient/apiclient";

const CompanySettings = () => {
  const [banners, setBanners] = useState([null, null, null, null]);
  const [bannerPreviews, setBannerPreviews] = useState([
    null,
    null,
    null,
    null,
  ]);
  const [offerBanners, setOfferBanners] = useState([null, null, null]);
  const [offerBannerPreviews, setOfferBannerPreviews] = useState([
    null,
    null,
    null,
  ]);
  const [loading, setLoading] = useState(false);

  /* ================= LOAD BANNERS ================= */
  useEffect(() => {
    const loadBanners = async () => {
      try {
        const res = await apiClient.get("/settings/company");

        if (res.data && res.data.banners && Array.isArray(res.data.banners)) {
          const previews = res.data.banners.map((banner) =>
            banner
              ? `${process.env.REACT_APP_IMAGE_URL}/${banner}?t=${Date.now()}`
              : null,
          );
          setBannerPreviews(previews);
        }

        if (
          res.data &&
          res.data.offerBanners &&
          Array.isArray(res.data.offerBanners)
        ) {
          const offerPreviews = res.data.offerBanners.map((banner) =>
            banner
              ? `${process.env.REACT_APP_IMAGE_URL}/${banner}?t=${Date.now()}`
              : null,
          );
          setOfferBannerPreviews(offerPreviews);
        }
      } catch (err) {
        console.error("Failed to load banners");
      }
    };

    loadBanners();
  }, []);

  /* ================= BANNER HANDLER ================= */
  const handleBannerChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const newBanners = [...banners];
    newBanners[index] = file;
    setBanners(newBanners);

    const newPreviews = [...bannerPreviews];
    newPreviews[index] = URL.createObjectURL(file);
    setBannerPreviews(newPreviews);
  };

  /* ================= OFFER BANNER HANDLER ================= */
  const handleOfferBannerChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const newOfferBanners = [...offerBanners];
    newOfferBanners[index] = file;
    setOfferBanners(newOfferBanners);

    const newPreviews = [...offerBannerPreviews];
    newPreviews[index] = URL.createObjectURL(file);
    setOfferBannerPreviews(newPreviews);
  };

  /* ================= SAVE BANNERS ================= */
  const handleSave = async () => {
    try {
      setLoading(true);

      const formData = new FormData();

      // Append promotional banners
      banners.forEach((banner, index) => {
        if (banner) {
          formData.append(`banner${index + 1}`, banner);
        }
      });

      // Append offer banners
      offerBanners.forEach((banner, index) => {
        if (banner) {
          formData.append(`offerBanner${index + 1}`, banner);
        }
      });

      await apiClient.post("/settings/company", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Banners saved successfully");
      setBanners([null, null, null, null]);
      setOfferBanners([null, null, null]);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save banners");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: "0px" }}>
      {/* PROMOTIONAL BANNERS SECTION */}
      <div>
        {/* HEADER */}
        <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50">
          <h1 className="text-xl font-bold text-slate-800">
            Mobile App Banners
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage 4 promotional banners for your mobile application.
            <span className="block text-xs font-semibold text-orange-500 mt-1">
              Recommended: 1080 x 540px (2:1 ratio) • Max 5MB
            </span>
          </p>
        </div>

        {/* CONTENT */}
        <div className="flex-1 w-full border-b border-slate-200">
          <div className="mb-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
              {[0, 1, 2, 3].map((index) => (
                <div key={index} className="p-2 border-b border-e">
                  {/* BANNER PREVIEW */}
                  <div className="w-full h-40 rounded-lg mb-2 border border-slate-200 bg-white flex items-center justify-center overflow-hidden">
                    {bannerPreviews[index] ? (
                      <img
                        src={bannerPreviews[index]}
                        alt={`Banner ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-sm text-slate-400">
                        Banner {index + 1} (max 5MB){" "}
                      </span>
                    )}
                  </div>

                  {/* UPLOAD */}
                  <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition-colors w-full justify-center shadow-sm">
                    <Upload className="w-4 h-4" />
                    Upload Image
                    <input
                      type="file"
                      accept="image/png, image/jpeg"
                      hidden
                      onChange={(e) => handleBannerChange(index, e)}
                    />
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* OFFER BANNERS SECTION */}
      <div>
        {/* HEADER */}
        <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50">
          <h1 className="text-xl font-bold text-slate-800">Offer Banners</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage 3 promotional offer banners for your mobile application.
            <span className="block text-xs font-semibold text-orange-500 mt-1">
              Recommended: 1080 x 300px (3.6:1 ratio) • Max 5MB
            </span>
          </p>
        </div>

        {/* CONTENT */}
        <div className="flex-1 w-full border-b border-slate-200">
          <div className="mb-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0">
              {[0, 1, 2].map((index) => (
                <div key={index} className="p-2 border-b border-e">
                  {/* OFFER BANNER PREVIEW */}
                  <div className="w-full h-40 rounded-lg mb-2 border border-slate-200 bg-white flex items-center justify-center overflow-hidden">
                    {offerBannerPreviews[index] ? (
                      <img
                        src={offerBannerPreviews[index]}
                        alt={`Offer Banner ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-sm text-slate-400">
                        Offer {index + 1} (max 5MB){" "}
                      </span>
                    )}
                  </div>

                  {/* UPLOAD */}
                  <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition-colors w-full justify-center shadow-sm">
                    <Upload className="w-4 h-4" />
                    Upload Image
                    <input
                      type="file"
                      accept="image/png, image/jpeg"
                      hidden
                      onChange={(e) => handleOfferBannerChange(index, e)}
                    />
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8 p-4 border-t border-slate-200 bg-white">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200 w-full sm:w-auto"
        >
          Cancel
        </button>

        <button
          onClick={handleSave}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-orange-500 px-6 py-2 text-sm font-medium text-white hover:bg-orange-600 transition disabled:opacity-70 disabled:cursor-not-allowed w-full sm:w-auto"
        >
          {loading ? "Saving..." : "Save Banners"}
        </button>
      </div>
    </div>
  );
};

export default CompanySettings;
